"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  TrendingUp,
  Info,
  AlertTriangle,
  Calculator,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  BRUT_ASGARI_UCRET,
  YILI,
  TUFE_2025_YILLIK,
  hesaplaMaasZammi,
  formatTL,
  type MaasZammiSonuc,
} from "./maas-zammi-engine";

const howToSchema = generateHowToSchema({
  title: "Maaş Zammı Nasıl Hesaplanır?",
  description: "Mevcut maaşınız ve zam oranıyla yeni maaşınızı hesaplayın.",
  totalTime: "PT1M",
  steps: [
    { name: "Mevcut Maaşı Girin", text: "Mevcut aylık brüt maaşınızı girin." },
    { name: "Zam Oranını Girin", text: "Uygulanacak zam oranını (%) girin." },
    {
      name: "Sonuçları İnceleyin",
      text: "Yeni maaş, zam tutarı ve enflasyon karşılaştırmasını görüntüleyin.",
    },
  ],
});

function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

export default function MaasZammiHesaplayiciClient() {
  const [mevcutMaas, setMevcutMaas] = useState("");
  const [zamOrani, setZamOrani] = useState("");
  const [hedefMaas, setHedefMaas] = useState("");
  const [mod, setMod] = useState<"oran" | "hedef">("oran");
  const [sonuc, setSonuc] = useState<MaasZammiSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const hesapla = useCallback(() => {
    setHata(null);
    const maas = parseFloat(mevcutMaas);
    if (!mevcutMaas || isNaN(maas) || maas <= 0) {
      setHata("Lütfen geçerli bir mevcut maaş giriniz.");
      setSonuc(null);
      return;
    }

    if (mod === "oran") {
      const oran = parseFloat(zamOrani);
      if (!zamOrani || isNaN(oran)) {
        setHata("Lütfen zam oranını giriniz.");
        setSonuc(null);
        return;
      }
      setSonuc(hesaplaMaasZammi({ mevcutMaas: maas, zamOrani: oran }));
    } else {
      const hedef = parseFloat(hedefMaas);
      if (!hedefMaas || isNaN(hedef) || hedef <= 0) {
        setHata("Lütfen hedef maaşı giriniz.");
        setSonuc(null);
        return;
      }
      setSonuc(hesaplaMaasZammi({ mevcutMaas: maas, hedefMaas: hedef }));
    }
  }, [mevcutMaas, zamOrani, hedefMaas, mod]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  return (
    <>
      <StructuredData data={[howToSchema]} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <section className="bg-gradient-to-br from-amber-500 to-orange-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "Maaş Zammı Hesaplayıcı",
                  href: "/araclar/maas-zammi-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />
            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <TrendingUp className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  Maaş Zammı Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  Enflasyon Karşılaştırmalı
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              Zam oranından yeni maaşınızı veya hedef maaştan gereken zam
              oranını hesaplayın. Enflasyon karşılaştırması dahil.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* Mod Seçimi */}
              <div className="flex gap-2 mb-8 p-1 bg-gray-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => setMod("oran")}
                  className={`flex-1 py-3 rounded-lg font-medium text-sm transition-colors ${mod === "oran" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
                >
                  Zam Oranından Hesapla
                </button>
                <button
                  type="button"
                  onClick={() => setMod("hedef")}
                  className={`flex-1 py-3 rounded-lg font-medium text-sm transition-colors ${mod === "hedef" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
                >
                  Hedef Maaştan Hesapla
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="mevcutMaas"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Mevcut Maaş
                  </label>
                  <div className="relative">
                    <input
                      id="mevcutMaas"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={mevcutMaas}
                      onChange={(e) => setMevcutMaas(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0,00"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-900"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      ₺
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMevcutMaas(BRUT_ASGARI_UCRET.toString())}
                    className="mt-2 text-xs text-amber-600 hover:text-amber-700 font-medium"
                  >
                    {YILI} Asgari Ücret ({formatTL(BRUT_ASGARI_UCRET)})
                  </button>
                </div>

                {mod === "oran" ? (
                  <div>
                    <label
                      htmlFor="zamOrani"
                      className="block text-sm font-semibold text-gray-900 mb-2"
                    >
                      Zam Oranı (%)
                    </label>
                    <div className="relative">
                      <input
                        id="zamOrani"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.1"
                        value={zamOrani}
                        onChange={(e) => setZamOrani(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="25"
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-900"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                        %
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label
                      htmlFor="hedefMaas"
                      className="block text-sm font-semibold text-gray-900 mb-2"
                    >
                      Hedef Maaş
                    </label>
                    <div className="relative">
                      <input
                        id="hedefMaas"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        value={hedefMaas}
                        onChange={(e) => setHedefMaas(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="0,00"
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-900"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                        ₺
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              <button
                type="button"
                onClick={hesapla}
                className="w-full bg-amber-500 text-white py-4 rounded-xl font-semibold hover:bg-amber-600 active:bg-amber-700 transition-colors shadow-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Hesapla
                </span>
              </button>
            </div>

            {sonuc && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-amber-500 text-white rounded-xl p-4">
                    <span className="text-xs text-amber-100 block">
                      Yeni Maaş
                    </span>
                    <span className="text-xl font-bold mt-1 block tabular-nums">
                      {formatTL(sonuc.yeniMaas)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Zam Oranı
                    </span>
                    <span className="text-xl font-bold text-amber-600 mt-1 block">
                      %{sonuc.zamOrani.toFixed(2)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Zam Tutarı
                    </span>
                    <span className="text-lg font-bold text-emerald-600 mt-1 block tabular-nums">
                      +{formatTL(sonuc.zamMiktari)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Yıllık Fark
                    </span>
                    <span className="text-lg font-bold text-blue-600 mt-1 block tabular-nums">
                      +{formatTL(sonuc.yillikFark)}
                    </span>
                  </div>
                </div>

                {/* Enflasyon Karşılaştırma */}
                <div className="bg-white rounded-xl p-5 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-3">
                    Enflasyon Karşılaştırması
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">TÜFE (Tahmini)</span>
                      <span className="font-medium">%{TUFE_2025_YILLIK}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Nominal Zam</span>
                      <span className="font-medium text-amber-600">
                        %{sonuc.zamOrani.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm border-t pt-2">
                      <span className="text-gray-900 font-semibold">
                        Reel Zam
                      </span>
                      <span
                        className={`font-bold ${sonuc.enflasyonKarsilastirma.reelZam >= 0 ? "text-emerald-600" : "text-red-600"}`}
                      >
                        {sonuc.enflasyonKarsilastirma.reelZam >= 0 ? "+" : ""}%
                        {sonuc.enflasyonKarsilastirma.reelZam.toFixed(2)}
                      </span>
                    </div>
                    <div className="mt-2">
                      <div className="h-3 rounded-full bg-gray-100 overflow-hidden relative">
                        <div
                          className="h-full bg-gray-300 rounded-full absolute left-0"
                          style={{
                            width: `${Math.min(100, (TUFE_2025_YILLIK / Math.max(sonuc.zamOrani, TUFE_2025_YILLIK)) * 100)}%`,
                          }}
                        />
                        <div
                          className={`h-full rounded-full absolute left-0 ${sonuc.enflasyonKarsilastirma.reelZam >= 0 ? "bg-emerald-500" : "bg-red-400"}`}
                          style={{
                            width: `${Math.min(100, (sonuc.zamOrani / Math.max(sonuc.zamOrani, TUFE_2025_YILLIK)) * 100)}%`,
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>TÜFE %{TUFE_2025_YILLIK}</span>
                        <span>Zam %{sonuc.zamOrani.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Sık Sorulan Sorular
          </h2>
          <div className="space-y-3">
            <SSSSoru
              soru="Reel zam oranı nedir?"
              cevap="Reel zam oranı, nominal zam oranından enflasyon oranının çıkarılmasıyla bulunur. Pozitifse alım gücünüz artmış, negatifse azalmış demektir."
            />
            <SSSSoru
              soru="Maaş zammı ne zaman yapılır?"
              cevap="İş Kanunu'nda zorunlu bir zam dönemi yoktur. Genellikle yılda 1 veya 2 kez (Ocak ve/veya Temmuz) yapılır. Toplu iş sözleşmesi varsa o tarihler geçerlidir."
            />
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="p-5 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-600">
                TÜFE oranı tahminidir. Kesin TÜFE verileri TÜİK tarafından
                açıklanır.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-2">
              Sonuc Yorumu ve Aksiyon Onerisi
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Zam oranini tek basina degerlendirmek yerine isveren maliyeti,
              gelir vergisi etkisi ve teklif sureciyle birlikte planlamak daha
              tutarli karar verir.
            </p>
            <div className="flex flex-wrap gap-2 text-sm">
              <Link
                href="/araclar/isveren-maliyeti-hesaplayici"
                className="text-indigo-600 hover:underline"
              >
                Isveren maliyeti hesabi
              </Link>
              <Link
                href="/yazilim/ucret-ve-yan-hak-yonetimi"
                className="text-indigo-600 hover:underline"
              >
                Ucret ve yan hak yonetimi
              </Link>
              <Link
                href="/cozumler/teklif-yonetimi"
                className="text-indigo-600 hover:underline"
              >
                Teklif sureci optimizasyonu
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Zam Planlama Kontrol Listesi
            </h2>
            <h3 className="text-base font-semibold text-gray-800 mb-2">
              Reel etki, butce ve teklif uyumu
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Zam kararini sadece nominal oranla degil, reel alım gucu ve toplam
              isveren maliyeti birlikte degerlendirmek gerekir.
            </p>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Aylik Takip Onerisi
            </h2>
            <p className="text-sm text-gray-600">
              Enflasyon, devir hizi ve teklif kabul orani aylik ritimde takip
              edilerek zam politikasinin etkisi olculebilir hale gelir.
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="maas-zammi-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-amber-500 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              Bordro Süreçlerinizi Otomatikleştirin
            </h2>
            <p className="text-amber-100 mb-6 max-w-xl mx-auto">
              IKAI ile maaş zammı, bordro ve tüm İK süreçlerini tek platformda
              yönetin.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-amber-700 px-6 py-3 rounded-xl font-semibold hover:bg-amber-50 transition-colors"
            >
              Ücretsiz Deneyin
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
